/**
 * Passkey API Service
 * @description Passkey 등록 및 인증 관련 API 연동
 */

import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
type PublicKeyCredentialCreationOptionsJSON = any;
type PublicKeyCredentialRequestOptionsJSON = any;
import { apiClient } from '@/utils/httpClient';

/**
 * Passkey 등록 시작
 * @returns PublicKeyCredentialCreationOptionsJSON
 */
export async function startPasskeyRegistration(adminUserId: string) {
  return apiClient.post<PublicKeyCredentialCreationOptionsJSON>('/admin/auth/passkey/registration/start', {
    adminUserId,
  }, { skipAuth: true });
}

/**
 * Passkey 등록 완료
 */
export async function finishPasskeyRegistration(
  adminUserId: string,
  credential: any // SimpleWebAuthn의 PublicKeyCredential 타입
) {
  // credential 객체 검증
  if (!credential || !credential.response) {
    throw new Error('Invalid credential: credential or credential.response is missing');
  }

  if (!credential.response.clientDataJSON) {
    throw new Error('Invalid credential: clientDataJSON is missing');
  }

  if (!credential.response.attestationObject) {
    throw new Error('Invalid credential: attestationObject is missing');
  }

  // credential 객체를 JSON으로 직렬화 가능한 형태로 변환
  // 모든 필수 필드가 존재하는지 확인
  console.log('[Passkey] Credential received:', {
    hasId: !!credential.id,
    hasRawId: !!credential.rawId,
    hasResponse: !!credential.response,
    hasClientDataJSON: !!credential.response?.clientDataJSON,
    hasAttestationObject: !!credential.response?.attestationObject,
    clientDataJSONType: typeof credential.response?.clientDataJSON,
    attestationObjectType: typeof credential.response?.attestationObject,
  });

  const response = {
    id: credential.id,
    rawId: credential.rawId ? arrayBufferToBase64(credential.rawId) : credential.id, // rawId가 없으면 id 사용
    response: {
      clientDataJSON: arrayBufferToBase64(credential.response.clientDataJSON),
      attestationObject: arrayBufferToBase64(credential.response.attestationObject),
      transports: credential.response.getTransports?.() || [],
    },
    type: credential.type || 'public-key',
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
    authenticatorAttachment: credential.authenticatorAttachment,
  };

  return apiClient.post<{
    success: boolean;
    passkeyId: string;
  }>('/admin/auth/passkey/registration/finish', {
    adminUserId,
    response,
  }, { skipAuth: true });
}

/**
 * ArrayBuffer를 Base64URL 문자열로 변환
 */
function arrayBufferToBase64(buffer: any): string {
  if (!buffer) {
    throw new Error(`Buffer is undefined or null. Buffer type: ${typeof buffer}, value: ${buffer}`);
  }
  
  if (typeof buffer === 'string') {
    // 이미 base64url 문자열인 경우 그대로 반환
    return buffer;
  }
  
  try {
    let bytes: Uint8Array;
    
    if (buffer instanceof ArrayBuffer) {
      bytes = new Uint8Array(buffer);
    } else if (buffer instanceof Uint8Array) {
      bytes = buffer;
    } else if (Array.isArray(buffer)) {
      bytes = new Uint8Array(buffer);
    } else {
      throw new Error(`Unsupported buffer type: ${typeof buffer}`);
    }
    
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    // Base64로 인코딩 후 URL-safe로 변환
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  } catch (error) {
    throw new Error(`Failed to convert buffer to base64url: ${error instanceof Error ? error.message : String(error)}. Buffer type: ${typeof buffer}`);
  }
}

/**
 * Passkey 인증 시작
 */
export async function startPasskeyAuthentication(adminUserId: string) {
  return apiClient.post<PublicKeyCredentialRequestOptionsJSON>('/admin/auth/passkey/authentication/start', {
    adminUserId,
  }, { skipAuth: true });
}

/**
 * Passkey 인증 완료 및 로그인
 */
export async function finishPasskeyAuthentication(
  adminUserId: string,
  credential: any // SimpleWebAuthn의 PublicKeyCredential 타입
) {
  // credential 객체 검증
  if (!credential || !credential.response) {
    throw new Error('Invalid credential: credential or credential.response is missing');
  }

  if (!credential.response.clientDataJSON) {
    throw new Error('Invalid credential: clientDataJSON is missing');
  }

  if (!credential.response.authenticatorData) {
    throw new Error('Invalid credential: authenticatorData is missing');
  }

  if (!credential.response.signature) {
    throw new Error('Invalid credential: signature is missing');
  }

  // credential 객체를 JSON으로 직렬화 가능한 형태로 변환
  const response = {
    id: credential.id,
    rawId: credential.rawId ? arrayBufferToBase64(credential.rawId) : undefined,
    response: {
      clientDataJSON: arrayBufferToBase64(credential.response.clientDataJSON),
      authenticatorData: arrayBufferToBase64(credential.response.authenticatorData),
      signature: arrayBufferToBase64(credential.response.signature),
      userHandle: credential.response.userHandle 
        ? arrayBufferToBase64(credential.response.userHandle)
        : null,
    },
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
  };

  return apiClient.post<{
    token: string;
    adminUserId: string;
    permissions: string[];
    tenantIds: string[] | null;
  }>('/admin/auth/passkey/authentication/finish', {
    adminUserId,
    response,
  }, { skipAuth: true });
}

/**
 * 등록된 Passkey 목록 조회
 */
export interface Passkey {
  id: string;
  deviceType: 'singleDevice' | 'multiDevice';
  transports: string[];
  createdAt: string;
  lastUsedAt: string | null;
}

export async function getPasskeys(): Promise<{ passkeys: Passkey[] }> {
  return apiClient.get<{ passkeys: Passkey[] }>('/admin/auth/passkeys');
}

/**
 * Passkey 삭제
 */
export async function deletePasskey(passkeyId: string): Promise<{ success: boolean }> {
  return apiClient.delete<{ success: boolean }>(`/admin/auth/passkeys/${passkeyId}`);
}

/**
 * Passkey 등록 프로세스 (전체 플로우)
 */
export async function registerPasskey(adminUserId: string): Promise<string> {
  // 1. 등록 옵션 가져오기
  const options = await startPasskeyRegistration(adminUserId);

  // 디버깅: options 객체 확인
  console.log('[Passkey] Registration options received:', {
    hasChallenge: !!options.challenge,
    challengeType: typeof options.challenge,
    challengeLength: options.challenge?.length,
    hasRp: !!options.rp,
    hasUser: !!options.user,
    user: options.user ? {
      id: options.user.id,
      idType: typeof options.user.id,
      idIsArray: Array.isArray(options.user.id),
      name: options.user.name,
    } : null,
  });

  // options 객체 검증
  if (!options.challenge) {
    throw new Error('Registration options missing challenge');
  }
  if (!options.rp) {
    throw new Error('Registration options missing rp');
  }
  if (!options.user) {
    throw new Error('Registration options missing user');
  }

  // SimpleWebAuthn v13: optionsJSON을 그대로 전달해야 내부에서 base64url을 처리함
  // user.id / excludeCredentials[].id / challenge는 모두 base64url 문자열이어야 함
  if (typeof options.user?.id !== 'string') {
    throw new Error(`Registration options user.id must be a string, got ${typeof options.user?.id}`);
  }

  if (options.excludeCredentials?.some((cred: any) => typeof cred.id !== 'string')) {
    const invalid = options.excludeCredentials.find((cred: any) => typeof cred.id !== 'string');
    throw new Error(`Registration options excludeCredentials.id must be string, got ${typeof invalid?.id}`);
  }

  if (typeof options.challenge !== 'string') {
    throw new Error(`Registration options challenge must be a string, got ${typeof options.challenge}`);
  }

  const finalOptions = options as PublicKeyCredentialCreationOptionsJSON;

  console.log('[Passkey] Final options prepared', {
    challenge: finalOptions.challenge,
    challengeType: typeof finalOptions.challenge,
    userIdType: typeof finalOptions.user?.id,
    excludeCredentialsCount: finalOptions.excludeCredentials?.length || 0,
  });

  // 2. 브라우저에서 Passkey 등록
  // SimpleWebAuthn v13: options 객체를 직접 전달
  const credential = await startRegistration({ optionsJSON: finalOptions });

  // 3. 등록 완료
  const result = await finishPasskeyRegistration(adminUserId, credential);

  return result.passkeyId;
}

/**
 * Passkey 인증 프로세스 (전체 플로우)
 */
export async function authenticateWithPasskey(adminUserId: string): Promise<{
  token: string;
  adminUserId: string;
  permissions: string[];
  tenantIds: string[] | null;
}> {
  // 1. 인증 옵션 가져오기
  const options = await startPasskeyAuthentication(adminUserId);

  // SimpleWebAuthn v13: optionsJSON을 그대로 전달해야 내부에서 base64url을 처리함
  if (typeof options.challenge !== 'string') {
    throw new Error(`Authentication options challenge must be a string, got ${typeof options.challenge}`);
  }

  if (options.allowCredentials?.some((cred: any) => typeof cred.id !== 'string')) {
    const invalid = options.allowCredentials.find((cred: any) => typeof cred.id !== 'string');
    throw new Error(`Authentication options allowCredentials.id must be string, got ${typeof invalid?.id}`);
  }

  const finalOptions = options as PublicKeyCredentialRequestOptionsJSON;

  // 2. 브라우저에서 Passkey 인증
  // SimpleWebAuthn v13: options 객체를 직접 전달
  const credential = await startAuthentication({ optionsJSON: finalOptions });

  // 3. 인증 완료 및 로그인
  return finishPasskeyAuthentication(adminUserId, credential);
}
